import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import ToolPrepPopup, {
  ToolPrepCloseContext,
} from "../../../shared/Popup/ToolPrepPopup";
import ToolPrepDownloadFlowModals from "../../../shared/Popup/ToolPrepDownloadFlowModals";
import PaddingPreview from "./PaddingPreview";
import PaddingPrepControls from "./PaddingPrepControls";
import ImagePickerModal from "../../../../../components/popups/imagePicker/ImagePickerModal";
import ToolPrepSourceCards from "../../../shared/ToolPrepSourceCards";
import { useToolPrepImageSource } from "../../../hooks/useToolPrepImageSource";
import { useToolPrepDownloadFlow } from "../../../hooks/useToolPrepDownloadFlow";
import { useSyncScrollbarWidth } from "../../../hooks/useSyncScrollbarWidth";
import { ensureBackgroundRemoved } from "../../../../../utils/ensureBackgroundRemoved";
import { createDefaultPaddingSettings, hasActivePaddingSettings } from "../../../../../utils/canvasPaddingLayout";
import { exportPaddedImageToPngBlob } from "../../../lib/exportPaddedImageToPngBlob";
import { withMinVisibleDuration } from "../../../lib/toolPrepCropApply";
import {
  TOOL_PREP_PREPARING_PREVIEW_LABEL,
  TOOL_PREP_RESULT_FOOTER_HINT,
} from "../../../../../utils/editorEntrySession";
import "./PaddingToolPrep.css";

const PADDING_THANK_YOU_COPY = {
  titleDownloading: "Downloading…",
  titleComplete: "Download complete",
  leadDownloading: "Please wait — we’re saving your file to this device.",
  leadComplete: "Your file is saved on this computer or phone. You can find it in your Downloads folder.",
  previewAlt: "Padded image download",
  primaryActionLabel: "Add new image",
};

/**
 * Editing Tools → Padding prep: upload, optional BG removal, per-side padding controls.
 * Independent from batch editor layout/padding (editorStore.layout).
 * Format/quality are chosen in Download Options after padding is applied.
 */
const PaddingToolPrep = ({
  originalImageUrl = null,
  imageFile = null,
  removeBgDefault = false,
  cachedCutoutUrl = null,
  onCutoutReady = null,
  onDone = () => {},
  onCancel = () => {},
}) => {
  const { getToken } = useAuth();
  const openedFromEditor = useContext(ToolPrepCloseContext) === "editor";
  const [paddingSettings, setPaddingSettings] = useState(() =>
    createDefaultPaddingSettings()
  );

  const [removeBg, setRemoveBg] = useState(Boolean(removeBgDefault));
  const [cutoutUrl, setCutoutUrl] = useState(cachedCutoutUrl || null);
  const [creationId, setCreationId] = useState(null);
  const [bgLoading, setBgLoading] = useState(false);
  const [bgError, setBgError] = useState(null);
  const [doneBusy, setDoneBusy] = useState(false);
  const [paddingApplied, setPaddingApplied] = useState(false);
  const [paddingBusy, setPaddingBusy] = useState(false);
  const [paddedPreviewUrl, setPaddedPreviewUrl] = useState(null);
  const [paddingApplyError, setPaddingApplyError] = useState(null);
  const bgRequestRef = useRef(null);
  const scrollBodyRef = useRef(null);
  const paddedPreviewUrlRef = useRef(null);

  const revokePaddedPreviewUrl = useCallback(() => {
    if (paddedPreviewUrlRef.current) {
      URL.revokeObjectURL(paddedPreviewUrlRef.current);
      paddedPreviewUrlRef.current = null;
    }
    setPaddedPreviewUrl(null);
  }, []);

  const {
    sourceImageUrl,
    resolvedFile,
    clearSource,
    openUploadPicker,
    imagePickerProps,
    uploadProgress,
    sourceFileName,
    sourceFileSizeLabel,
  } = useToolPrepImageSource({
    originalImageUrl,
    imageFile,
    busy: bgLoading || doneBusy || paddingBusy,
    onSourceChange: () => {
      setCutoutUrl(null);
      setCreationId(null);
      if (!removeBgDefault) setRemoveBg(false);
      setBgError(null);
      setPaddingApplyError(null);
      setPaddingSettings(createDefaultPaddingSettings());
      setPaddingApplied(false);
      revokePaddedPreviewUrl();
    },
  });

  const downloadFlow = useToolPrepDownloadFlow({
    variant: "editing",
    sourceFileName,
    fallbackDownloadName: "padded.png",
    getToken,
    toolKey: "padding",
    shareTitle: "Padding tool",
    shareText:
      "I added perfect padding to my photo with LazyStudio's Padding tool!",
    thankYouCopy: PADDING_THANK_YOU_COPY,
  });

  const { downloadBusy } = downloadFlow;

  React.useEffect(() => {
    if (cachedCutoutUrl) setCutoutUrl(cachedCutoutUrl);
  }, [cachedCutoutUrl]);

  useSyncScrollbarWidth(scrollBodyRef, [
    sourceImageUrl,
    paddingSettings.top,
    paddingSettings.right,
    paddingSettings.bottom,
    paddingSettings.left,
    paddingSettings.unit,
    paddingSettings.fillColor,
    paddingSettings.cornerRadius,
    paddingApplied,
  ]);

  const runBackgroundRemoval = useCallback(async () => {
    if (cutoutUrl?.startsWith("blob:") || cutoutUrl?.startsWith("data:")) {
      return { cutoutUrl, creationId };
    }

    const requestId = Symbol("bg");
    bgRequestRef.current = requestId;
    setBgLoading(true);
    setBgError(null);

    try {
      const token = await getToken();
      const { cutoutUrl: nextUrl, creationId: cid } =
        await ensureBackgroundRemoved({
          token,
          imageFile: resolvedFile,
          imageUrl: sourceImageUrl,
          cachedCutoutUrl: cutoutUrl || cachedCutoutUrl,
        });
      if (bgRequestRef.current !== requestId) return null;
      setCutoutUrl(nextUrl);
      if (cid != null) setCreationId(cid);
      onCutoutReady?.(nextUrl);
      return { cutoutUrl: nextUrl, creationId: cid ?? creationId };
    } catch (err) {
      if (bgRequestRef.current === requestId) {
        setBgError(err?.message || "Failed to remove background");
      }
      throw err;
    } finally {
      if (bgRequestRef.current === requestId) {
        setBgLoading(false);
      }
    }
  }, [
    cachedCutoutUrl,
    creationId,
    cutoutUrl,
    getToken,
    onCutoutReady,
    resolvedFile,
    sourceImageUrl,
  ]);

  const handleRemoveBgToggle = async () => {
    if (!sourceImageUrl || bgLoading) return;
    const next = !removeBg;
    setRemoveBg(next);
    if (!next) {
      setBgError(null);
      return;
    }
    try {
      await runBackgroundRemoval();
    } catch {
      setRemoveBg(false);
    }
  };

  const previewImageUrl =
    removeBg && cutoutUrl ? cutoutUrl : sourceImageUrl;

  const handleSettingsChange = (nextSettings) => {
    setPaddingApplied(false);
    setPaddingApplyError(null);
    revokePaddedPreviewUrl();
    setPaddingSettings(nextSettings);
  };

  const handleApplyPadding = async () => {
    const url = previewImageUrl;
    if (!url || paddingBusy || !hasActivePaddingSettings(paddingSettings)) return;

    setPaddingApplyError(null);
    setPaddingBusy(true);
    try {
      const blob = await withMinVisibleDuration(
        exportPaddedImageToPngBlob(url, paddingSettings)
      );
      revokePaddedPreviewUrl();
      const nextUrl = URL.createObjectURL(blob);
      paddedPreviewUrlRef.current = nextUrl;
      setPaddedPreviewUrl(nextUrl);
      setPaddingApplied(true);
    } catch (err) {
      setPaddingApplyError(err?.message || "Could not apply padding.");
    } finally {
      setPaddingBusy(false);
    }
  };

  const handleApplyToEditor = () => {
    const imageUrl = removeBg && cutoutUrl ? cutoutUrl : sourceImageUrl;
    if (!imageUrl || !hasActivePaddingSettings(paddingSettings)) return;
    onDone({
      imageUrl,
      creationId: removeBg ? creationId : null,
      removeBg,
      padding: { ...paddingSettings },
    });
  };

  const handleAdjustPadding = () => {
    setPaddingApplyError(null);
    setPaddingApplied(false);
    revokePaddedPreviewUrl();
  };

  const handleDownload = useCallback(() => {
    if (!paddedPreviewUrl) return;
    downloadFlow.openDownloadOptions({
      previewUrl: paddedPreviewUrl,
      resolveImageUrl: async () => paddedPreviewUrl,
    });
  }, [downloadFlow, paddedPreviewUrl]);

  useEffect(
    () => () => {
      if (paddedPreviewUrlRef.current) {
        URL.revokeObjectURL(paddedPreviewUrlRef.current);
        paddedPreviewUrlRef.current = null;
      }
    },
    []
  );

  const controlsLocked =
    paddingApplied || paddingBusy || bgLoading || doneBusy || downloadBusy;

  const paddingSettingsReady = hasActivePaddingSettings(paddingSettings);
  const canApplyPadding =
    Boolean(sourceImageUrl) && paddingSettingsReady && !paddingBusy;

  const configureFooterHint = !sourceImageUrl
    ? "Upload an image to start adding padding."
    : !paddingSettingsReady
      ? "Set padding, fill color, or round corners, then click Apply Padding."
      : "Adjust padding in the preview, then click Apply Padding.";

  const handleDone = async () => {
    setDoneBusy(true);
    try {
      if (paddingApplied && paddedPreviewUrl) {
        onDone({
          imageUrl: paddedPreviewUrl,
          creationId: removeBg ? creationId : null,
          removeBg,
          padding: createDefaultPaddingSettings(),
          paddingBaked: true,
        });
        return;
      }

      let imageUrl = sourceImageUrl;
      let doneCreationId = creationId;
      if (removeBg) {
        if (cutoutUrl) {
          imageUrl = cutoutUrl;
        } else {
          const removed = await runBackgroundRemoval();
          imageUrl = removed?.cutoutUrl;
          doneCreationId = removed?.creationId ?? doneCreationId;
        }
      }
      if (!imageUrl) {
        throw new Error("No image ready");
      }
      if (
        typeof imageUrl === "string" &&
        /^https?:\/\//i.test(imageUrl) &&
        !imageUrl.startsWith("blob:")
      ) {
        throw new Error(
          "Background-removed image could not be loaded for the editor (CORS). Please try Remove Background again."
        );
      }
      onDone({
        imageUrl,
        creationId: removeBg ? doneCreationId : null,
        removeBg,
        padding: { ...paddingSettings },
      });
    } catch (err) {
      setBgError(err?.message || "Could not finish padding setup");
    } finally {
      setDoneBusy(false);
    }
  };

  const leftContent = (
    <div className="ptp-panel">
      <div className="ptp-scroll-body tool-prep-scroll" ref={scrollBodyRef}>
        <div className="ptp-source-block">
          <ToolPrepSourceCards
            originalImageUrl={sourceImageUrl}
            previewImageUrl={removeBg && cutoutUrl ? cutoutUrl : null}
            removeBgActive={removeBg}
            cutoutPending={bgLoading && !cutoutUrl}
            onUploadClick={openUploadPicker}
            onRemove={clearSource}
            uploadProgress={uploadProgress}
            sourceName={sourceFileName}
            sourceSizeLabel={sourceFileSizeLabel}
            actionsDisabled={bgLoading || doneBusy || downloadBusy}
            removeBgControls={{
              active: removeBg,
              onToggle: handleRemoveBgToggle,
              disabled:
                bgLoading || !sourceImageUrl || paddingApplied || paddingBusy,
              // Do not set locked after apply — ToolPrepRemoveBgBlock treats
              // locked as visually ON (meant for required-BG tools).
              bio: "Optional — remove the backdrop before adding padding around the subject.",
              error: bgError,
            }}
          />
        </div>
        <PaddingPrepControls
          settings={paddingSettings}
          onChange={handleSettingsChange}
          disabled={!sourceImageUrl || controlsLocked}
        />
      </div>
    </div>
  );

  return (
    <>
      <ImagePickerModal {...imagePickerProps} />
      <ToolPrepPopup
        toolKey="padding"
        title="Padding"
        left={leftContent}
        preview={
          <PaddingPreview
            imageUrl={previewImageUrl}
            settings={paddingSettings}
            loading={bgLoading}
            applying={paddingBusy}
            resultMode={paddingApplied}
            resultImageUrl={paddedPreviewUrl}
            showSubjectBox={Boolean(removeBg && cutoutUrl && !paddingApplied)}
            emptyLabel={
              uploadProgress != null && !previewImageUrl
                ? TOOL_PREP_PREPARING_PREVIEW_LABEL
                : undefined
            }
          />
        }
        onDone={
          openedFromEditor
            ? handleApplyToEditor
            : paddingApplied
              ? handleDone
              : handleApplyPadding
        }
        onCancel={onCancel}
        confirmClose={Boolean(sourceImageUrl)}
        onDownload={openedFromEditor ? null : paddingApplied ? handleDownload : null}
        onOpenInEditor={
          openedFromEditor ? null : paddingApplied ? handleDone : null
        }
        onSecondaryAction={
          openedFromEditor ? null : paddingApplied ? handleAdjustPadding : null
        }
        secondaryActionLabel="Adjust padding"
        busy={
          openedFromEditor
            ? bgLoading
            : paddingBusy || doneBusy || bgLoading || downloadBusy
        }
        busyLabel="Applying padding…"
        showDone={openedFromEditor || !paddingApplied}
        doneDisabled={!canApplyPadding}
        doneLabel="Apply Padding"
        footerActionOrder={
          paddingApplied
            ? ["cancel", "secondary", "download", "openInEditor"]
            : ["cancel", "primary"]
        }
        openInEditorLabel="Open in Editor"
        footerHint={
          paddingApplyError ||
          (paddingApplied ? TOOL_PREP_RESULT_FOOTER_HINT : configureFooterHint)
        }
        footerHintClassName={
          paddingApplyError ? "tool-prep-popup__footer-hint--error" : ""
        }
      />
      <ToolPrepDownloadFlowModals
        flow={downloadFlow}
        toolKey="padding"
        onPrimaryAction={() => {
          downloadFlow.handleThankYouDismiss();
          clearSource();
        }}
        onBackToDashboard={onCancel}
      />
    </>
  );
};

export default PaddingToolPrep;
